import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import {
  Key,
  Gauge,
  Download,
  FileText,
  User,
  Home,
  TrendingUp,
  Wrench,
  PenLine,
  DoorOpen,
  DoorClosed,
  Wallet,
  AlertTriangle,
  Clock,
  Plus,
  Pencil,
  Save,
  MessageCircle,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { detalleContrato } from "../data/contratoDetalle";
import { pesos, fechaCorta } from "../lib/format";
import { generarDocumentoPDF, generarActaPDF } from "../lib/docPdf";
import { useBrand } from "../context/BrandContext";
import { PhotoInput } from "../components/PhotoInput";
import RescisionWizard from "./RescisionWizard";

const TABS = [
  { id: "condiciones", label: "Condiciones" },
  { id: "costos", label: "Costos" },
  { id: "cuenta", label: "Cuenta cte." },
  { id: "entrada", label: "Entrada" },
  { id: "salida", label: "Salida" },
  { id: "arreglos", label: "Arreglos" },
  { id: "timeline", label: "Línea de tiempo" },
  { id: "documentos", label: "Documentos" },
];

const TIMELINE_ICONS = {
  firma: PenLine,
  entrada: DoorOpen,
  pago: Wallet,
  ajuste: TrendingUp,
  arreglo: Wrench,
  aviso: AlertTriangle,
  salida: DoorClosed,
};

export default function FichaContrato({ contrato, onClose, onEstadoChange }) {
  const { brand } = useBrand();
  const [cuenta, setCuenta] = useState(() => (contrato ? detalleContrato(contrato).cuenta : []));
  const [arreglos, setArreglos] = useState(() => (contrato ? detalleContrato(contrato).arreglos : []));
  const [editCuenta, setEditCuenta] = useState(false);
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [checkout, setCheckout] = useState(() => (contrato ? detalleContrato(contrato).checkout : null));
  const [estado, setEstado] = useState(() => contrato?.estado);
  const [form, setForm] = useState({ titulo: "", ambiente: "Cocina", urgencia: "Media", paga: "Propietario", presupuesto: "", proveedor: "", fotoAntes: null, fotoDespues: null });

  if (!contrato) return <Sheet open={false} />;
  const c = contrato;
  const det = detalleContrato(c);
  const saldoActual = cuenta.length ? cuenta[cuenta.length - 1].saldo : 0;

  const setFoto = (id, which, dataUrl) =>
    setArreglos((prev) => prev.map((a) => (a.id === id ? { ...a, fotos: { ...a.fotos, [which]: dataUrl } } : a)));

  const completarRescision = (co) => {
    setCheckout(co);
    setEstado("En salida");
    onEstadoChange?.(c.id, "En salida");
    toast.success("Rescisión iniciada y check-out registrado. Se notifica a inquilino y propietario.");
  };

  const updateMov = (idx, field, value) =>
    setCuenta((prev) => recalcSaldos(prev.map((m, i) => (i === idx ? { ...m, [field]: Math.max(0, Number(value) || 0) } : m))));
  const updateEstadoMov = (idx, value) =>
    setCuenta((prev) => prev.map((m, i) => (i === idx ? { ...m, estado: value } : m)));

  const aprobar = (a) => {
    setArreglos((prev) => prev.map((x) => (x.id === a.id ? { ...x, aprobacion: "Aprobado", estado: "Aprobado" } : x)));
    if (a.paga === "Inquilino") {
      setCuenta((prev) => recalcSaldos(prev.map((m, i) => (i === prev.length - 1 ? { ...m, arreglos: (m.arreglos || 0) + a.presupuesto } : m))));
      toast.success(`Arreglo aprobado. ${pesos(a.presupuesto)} imputados en la cuenta corriente del inquilino.`);
    } else {
      toast.success(`Arreglo aprobado. ${pesos(a.presupuesto)} irán en la liquidación del propietario.`);
    }
  };
  const rechazar = (a) => {
    setArreglos((prev) => prev.map((x) => (x.id === a.id ? { ...x, aprobacion: "Rechazado" } : x)));
    toast.error("Presupuesto rechazado. Se avisa al inquilino.");
  };
  const pedirPresupuesto = (a) => {
    const conFoto = a.fotos.antes ? "con la foto del antes adjunta" : "sin foto adjunta (podés subir una en la tarjeta)";
    setArreglos((prev) => prev.map((x) => (x.id === a.id && x.estado === "Reportado" ? { ...x, estado: "Presupuestado" } : x)));
    toast.success(`Pedido de presupuesto enviado por WhatsApp a ${a.proveedor} ${conFoto}.`);
  };
  const agregarArreglo = () => {
    if (!form.titulo.trim()) {
      toast.error("Poné un título para el arreglo.");
      return;
    }
    const nuevo = {
      id: `${c.id}-arr-nuevo-${Date.now()}`,
      titulo: form.titulo,
      ambiente: form.ambiente,
      fechaReporte: new Date().toISOString(),
      urgencia: form.urgencia,
      paga: form.paga,
      presupuesto: Number(form.presupuesto) || 0,
      aprobacion: "Pendiente",
      proveedor: form.proveedor || "Por asignar",
      estado: "Reportado",
      fotos: { antes: form.fotoAntes || null, despues: form.fotoDespues || null },
    };
    setArreglos((prev) => [nuevo, ...prev]);
    setNuevoOpen(false);
    setForm({ titulo: "", ambiente: "Cocina", urgencia: "Media", paga: "Propietario", presupuesto: "", proveedor: "", fotoAntes: null, fotoDespues: null });
    toast.success("Arreglo reportado. Queda pendiente de aprobación del propietario.");
  };

  const descargarDoc = (doc) => {
    if (doc.acta) {
      generarActaPDF({ tipo: doc.acta, contrato: c, detalle: det, agencia: brand.nombre, color: brand.color });
    } else {
      generarDocumentoPDF({
        titulo: doc.tipo,
        subtitulo: doc.nombre,
        agencia: brand.nombre,
        color: brand.color,
        nombreArchivo: `${doc.id}.pdf`,
        lineas: [
          `Propiedad: ${c.direccion}, ${c.barrio}`,
          `Inquilino: ${c.inquilino.nombre} · Propietario: ${c.propietario.nombre}`,
          `Monto mensual: ${pesos(c.montoMensual)}`,
          `Índice de ajuste: ${c.indice} · cada ${c.periodicidadAjusteMeses} meses`,
          `Vigencia: ${fechaCorta(c.inicio)} → ${fechaCorta(c.vencimiento)}`,
        ],
      });
    }
    toast.success("Documento descargado en PDF.");
  };

  return (
    <Sheet open={!!contrato} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="ficha-contrato">
        <SheetHeader>
          <SheetTitle className="text-left">{c.direccion}, {c.barrio}</SheetTitle>
          <SheetDescription className="text-left">
            Ficha completa del alquiler: condiciones, costos, cuenta corriente, entrada, salida, arreglos, línea de tiempo y documentos.
          </SheetDescription>
        </SheetHeader>

        {/* 1 · Resumen */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <StatusBadge estado={estado} />
          {c.situacion && <StatusBadge estado={c.situacion} />}
          <StatusBadge estado={c.indice} />
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="p-3 rounded-md border-slate-200 shadow-none">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 flex items-center gap-1"><User className="h-3 w-3" /> Inquilino</p>
            <p className="text-sm font-medium text-slate-900 mt-1">{c.inquilino.nombre}</p>
            <p className="text-xs text-slate-500">{c.inquilino.telefono}</p>
          </Card>
          <Card className="p-3 rounded-md border-slate-200 shadow-none">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 flex items-center gap-1"><Home className="h-3 w-3" /> Propietario</p>
            <p className="text-sm font-medium text-slate-900 mt-1">{c.propietario.nombre}</p>
            <p className="text-xs text-slate-500">{c.propietario.telefono}</p>
          </Card>
          <Card className={`p-3 rounded-md shadow-none ${saldoActual > 0 ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 flex items-center gap-1"><Wallet className="h-3 w-3" /> Saldo actual</p>
            <p className={`text-lg font-bold mt-1 ${saldoActual > 0 ? "text-red-800" : "text-emerald-800"}`} data-testid="ficha-saldo">
              {saldoActual > 0 ? `Debe ${pesos(saldoActual)}` : "Sin deuda"}
            </p>
            <p className="text-xs text-slate-500">Cuenta corriente del contrato</p>
          </Card>
        </div>

        <Tabs defaultValue="condiciones" className="mt-5">
          <TabsList className="rounded-md w-full justify-start overflow-x-auto flex-nowrap h-auto">
            {TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`ficha-tab-${t.id}`} className="rounded-md whitespace-nowrap">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* 2 · Condiciones */}
          <TabsContent value="condiciones" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="p-4 rounded-md border-slate-200 shadow-none">
                <Sub t="Plazo" />
                <Kv l="Inicio" v={fechaCorta(det.plazo.inicio)} />
                <Kv l="Fin" v={fechaCorta(det.plazo.fin)} />
                <Kv l="Duración" v={`${det.plazo.duracionMeses} meses`} />
                <Kv
                  l="Faltan"
                  v={det.plazo.diasParaVencer >= 0 ? `${det.plazo.diasParaVencer} días` : `Venció hace ${-det.plazo.diasParaVencer} días`}
                />
              </Card>
              <Card className="p-4 rounded-md border-slate-200 shadow-none">
                <Sub t="Pago" />
                <Kv l="Día de pago" v={det.pago.diaPago} />
                <Kv l="Punitorios por mora" v={det.pago.punitorios} />
                <Sub t="Depósito" />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">{pesos(det.deposito.monto)}</span>
                  <StatusBadge estado={det.deposito.estado} />
                </div>
              </Card>
              <Card className="p-4 rounded-md border-slate-200 shadow-none">
                <Sub t="Ajuste" />
                <Kv l="Monto actual" v={pesos(c.montoMensual)} />
                <Kv l="Índice" v={c.indice} />
                <Kv l="Cada" v={`${c.periodicidadAjusteMeses} meses`} />
                <Kv l="Próximo ajuste" v={fechaCorta(c.proximaActualizacion)} />
                <Kv l="Monto estimado" v={pesos(c.proximoMontoEstimado)} />
              </Card>
              <Card className="p-4 rounded-md border-slate-200 shadow-none">
                <Sub t="Garantía" />
                <Kv l="Tipo" v={det.garantia.tipo} />
                <p className="mt-1 text-sm text-slate-600">{det.garantia.datos}</p>
              </Card>
            </div>
            <Card className="rounded-md border-slate-200 shadow-none overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Historial de ajustes</div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Fecha</TableHead>
                    <TableHead>Índice</TableHead>
                    <TableHead>%</TableHead>
                    <TableHead className="text-right">Monto anterior</TableHead>
                    <TableHead className="text-right">Monto nuevo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {det.historialAjustes.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm text-slate-600">{fechaCorta(a.fecha)}</TableCell>
                      <TableCell><StatusBadge estado={a.indice} /></TableCell>
                      <TableCell className="text-sm text-slate-600">{a.pct}%</TableCell>
                      <TableCell className="text-right text-sm text-slate-500">{pesos(a.montoAnterior)}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{pesos(a.montoNuevo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* 3 · Costos */}
          <TabsContent value="costos" className="space-y-4 mt-4">
            <Card className="rounded-md border-slate-200 shadow-none overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Expensas, ABL, servicios y cargos</div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Concepto</TableHead>
                    <TableHead>Quién paga</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <CostoRow concepto="Expensas ordinarias" paga="Inquilino" alDia monto={det.costos.expensasOrdinarias} />
                  {det.costos.expensasExtraordinarias && (
                    <CostoRow concepto={`Expensas extraordinarias (${det.costos.expensasExtraordinarias.motivo})`} paga="Propietario" alDia monto={det.costos.expensasExtraordinarias.monto} />
                  )}
                  <CostoRow concepto="ABL" paga="Inquilino" alDia monto={det.costos.abl} />
                  {det.costos.servicios.map((s) => (
                    <CostoRow key={s.nombre} concepto={`Servicio · ${s.nombre}`} paga={s.paga} alDia={s.alDia} monto={s.monto} />
                  ))}
                  <CostoRow concepto="Seguro del depto" paga={det.costos.seguroDepto.paga} alDia={det.costos.seguroDepto.alDia} monto={det.costos.seguroDepto.monto} />
                  <CostoRow concepto="Honorarios administración (7%)" paga={det.costos.honorarios.paga} alDia monto={det.costos.honorarios.monto} />
                </TableBody>
              </Table>
            </Card>
            <Card className="p-4 rounded-md border-slate-200 shadow-none">
              <Sub t="Total mensual que paga el inquilino" />
              <div className="space-y-1 mt-1">
                {det.costos.desgloseInquilino.map((x, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-500">{x.concepto}</span>
                    <span className="text-slate-800">{pesos(x.monto)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-2 border-t border-slate-100">
                  <span className="text-sm font-semibold text-slate-900">Total mensual</span>
                  <span className="text-base font-bold text-slate-900" data-testid="ficha-total-mensual">{pesos(det.costos.totalMensualInquilino)}</span>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* 4 · Cuenta corriente */}
          <TabsContent value="cuenta" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-500">Movimientos mes a mes. Podés editar montos y punitorios.</p>
              <Button
                size="sm"
                variant={editCuenta ? "default" : "outline"}
                className={`rounded-md ${editCuenta ? "btn-brand" : ""}`}
                data-testid="ficha-cuenta-edit-toggle"
                onClick={() => {
                  if (editCuenta) toast.success("Cambios guardados en la cuenta corriente.");
                  setEditCuenta((v) => !v);
                }}
              >
                {editCuenta ? <><Save className="mr-1.5 h-4 w-4" /> Guardar</> : <><Pencil className="mr-1.5 h-4 w-4" /> Editar</>}
              </Button>
            </div>
            <Card className="rounded-md border-slate-200 shadow-none overflow-x-auto">
              <Table data-testid="ficha-cuenta-corriente">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Cobrado</TableHead>
                    <TableHead className="text-right">Debe</TableHead>
                    <TableHead className="text-right">Punitorios</TableHead>
                    <TableHead className="text-right">Arreglos</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cuenta.map((m, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm text-slate-700 capitalize whitespace-nowrap">{m.periodo}</TableCell>
                      {editCuenta ? (
                        <>
                          <TableCell className="text-right"><NumCell value={m.cobrado} onChange={(v) => updateMov(i, "cobrado", v)} testId={`ficha-mov-cobrado-${i}`} /></TableCell>
                          <TableCell className="text-right"><NumCell value={m.debe} onChange={(v) => updateMov(i, "debe", v)} testId={`ficha-mov-debe-${i}`} /></TableCell>
                          <TableCell className="text-right"><NumCell value={m.punitorios} onChange={(v) => updateMov(i, "punitorios", v)} testId={`ficha-mov-punitorios-${i}`} /></TableCell>
                          <TableCell className="text-right"><NumCell value={m.arreglos} onChange={(v) => updateMov(i, "arreglos", v)} /></TableCell>
                          <TableCell className="text-right text-sm font-medium">{pesos(m.saldo)}</TableCell>
                          <TableCell>
                            <Select value={m.estado} onValueChange={(v) => updateEstadoMov(i, v)}>
                              <SelectTrigger className="h-8 w-28 rounded-md text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Pagado">Pagado</SelectItem>
                                <SelectItem value="Pendiente">Pendiente</SelectItem>
                                <SelectItem value="Atrasado">Atrasado</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-right text-sm text-emerald-700">{m.cobrado ? pesos(m.cobrado) : "—"}</TableCell>
                          <TableCell className="text-right text-sm text-red-700">{m.debe ? pesos(m.debe) : "—"}</TableCell>
                          <TableCell className="text-right text-sm text-slate-500">{m.punitorios ? pesos(m.punitorios) : "—"}</TableCell>
                          <TableCell className="text-right text-sm text-slate-500">{m.arreglos ? pesos(m.arreglos) : "—"}</TableCell>
                          <TableCell className="text-right text-sm font-medium">{m.saldo ? pesos(m.saldo) : "—"}</TableCell>
                          <TableCell><StatusBadge estado={m.estado} /></TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <div className="mt-3 flex justify-end">
              <div className={`rounded-md border px-4 py-2 ${saldoActual > 0 ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
                <span className="text-xs text-slate-500">Saldo actual: </span>
                <span className={`text-sm font-bold ${saldoActual > 0 ? "text-red-800" : "text-emerald-800"}`} data-testid="ficha-saldo-cuenta">
                  {saldoActual > 0 ? pesos(saldoActual) : "Sin deuda"}
                </span>
              </div>
            </div>
          </TabsContent>

          {/* 5 · Entrada */}
          <TabsContent value="entrada" className="space-y-4 mt-4">
            <Card className="p-4 rounded-md border-slate-200 shadow-none">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Kv l="Fecha de entrada" v={fechaCorta(det.checkin.fecha)} />
                <Kv l="Llaves entregadas" v={`${det.checkin.llaves} juegos`} icon={Key} />
                <div className="col-span-2">
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 flex items-center gap-1"><Gauge className="h-3 w-3" /> Lectura de medidores</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(det.checkin.medidores).map(([k, v]) => (
                      <span key={k} className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 capitalize">{k}: {v}</span>
                    ))}
                  </div>
                </div>
              </div>
              <Button size="sm" className="btn-brand rounded-md mt-4" data-testid="ficha-acta-entrada-pdf"
                onClick={() => { generarActaPDF({ tipo: "entrada", contrato: c, detalle: det, agencia: brand.nombre, color: brand.color }); toast.success("Acta de entrega descargada en PDF."); }}>
                <Download className="mr-1.5 h-4 w-4" /> Acta de entrega firmada (PDF)
              </Button>
            </Card>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">Inventario por ambiente</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {det.checkin.inventario.map((inv, i) => (
                <Card key={i} className="rounded-md border-slate-200 shadow-none overflow-hidden">
                  <img src={inv.foto} alt={inv.ambiente} className="h-24 w-full object-cover" />
                  <div className="p-3">
                    <p className="text-sm font-semibold text-slate-900 mb-2">{inv.ambiente}</p>
                    <div className="space-y-1.5">
                      {inv.items.map((it, k) => (
                        <div key={k} className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">{it.item}</span>
                          <StatusBadge estado={it.estado} />
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* 6 · Salida */}
          <TabsContent value="salida" className="space-y-4 mt-4">
            {!checkout ? (
              <Card className="p-6 rounded-md border-slate-200 shadow-none text-center">
                <DoorClosed className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Todavía no hay proceso de salida</p>
                <p className="text-xs text-slate-400 mt-1 mb-4">Iniciá la rescisión y completá el check-out paso a paso: aviso, inspección de salida y devolución del depósito.</p>
                {estado === "Finalizado" ? (
                  <p className="text-xs text-slate-400">Este contrato ya está finalizado.</p>
                ) : (
                  <Button className="btn-brand rounded-md" data-testid="ficha-iniciar-rescision" onClick={() => setWizardOpen(true)}>
                    <DoorClosed className="mr-1.5 h-4 w-4" /> Iniciar rescisión
                  </Button>
                )}
              </Card>
            ) : (
              <>
                <Card className="p-4 rounded-md border-slate-200 shadow-none grid grid-cols-2 gap-4 text-sm">
                  <Kv l="Aviso de rescisión" v={fechaCorta(checkout.aviso.fecha)} />
                  <Kv l="Avisó" v={checkout.aviso.quienAviso} />
                  <Kv l="Fecha prevista de salida" v={fechaCorta(checkout.fechaPrevista)} />
                  <div className="flex items-end">
                    <Button size="sm" className="btn-brand rounded-md" data-testid="ficha-acta-salida-pdf"
                      onClick={() => { generarActaPDF({ tipo: "salida", contrato: c, detalle: det, agencia: brand.nombre, color: brand.color }); toast.success("Acta de salida descargada en PDF."); }}>
                      <Download className="mr-1.5 h-4 w-4" /> Acta de salida (PDF)
                    </Button>
                  </div>
                </Card>
                <Card className="rounded-md border-slate-200 shadow-none overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Inspección de salida vs entrada</div>
                  <div className="overflow-x-auto">
                    <Table data-testid="ficha-inspeccion-salida">
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Ambiente</TableHead>
                          <TableHead>Ítem</TableHead>
                          <TableHead>Entrada</TableHead>
                          <TableHead>Salida</TableHead>
                          <TableHead>Acción</TableHead>
                          <TableHead className="text-right">Costo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {checkout.inspeccion.map((x, i) => (
                          <TableRow key={i} className={x.costo > 0 ? "bg-red-50/50" : ""}>
                            <TableCell className="text-sm text-slate-600">{x.ambiente}</TableCell>
                            <TableCell className="text-sm text-slate-600">{x.item}</TableCell>
                            <TableCell><StatusBadge estado={x.entrada} /></TableCell>
                            <TableCell><StatusBadge estado={x.salida} /></TableCell>
                            <TableCell className="text-sm text-slate-600">{x.accion}</TableCell>
                            <TableCell className="text-right text-sm font-medium">{x.costo > 0 ? pesos(x.costo) : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
                <Card className="p-4 rounded-md border-slate-200 shadow-none">
                  <Sub t="Devolución del depósito" />
                  <div className="space-y-1 mt-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Depósito</span><span className="text-slate-800">{pesos(checkout.devolucion.montoBase)}</span></div>
                    {checkout.devolucion.descuentos.map((d, i) => (
                      <div key={i} className="flex justify-between"><span className="text-slate-500">{d.concepto}</span><span className="text-red-700">-{pesos(d.monto)}</span></div>
                    ))}
                    <div className="flex justify-between pt-2 mt-2 border-t border-slate-100">
                      <span className="font-semibold text-slate-900">Saldo a devolver</span>
                      <span className="font-bold text-emerald-700" data-testid="ficha-deposito-saldo">{pesos(checkout.devolucion.saldo)}</span>
                    </div>
                  </div>
                </Card>
              </>
            )}
            <RescisionWizard
              key={wizardOpen}
              open={wizardOpen}
              onOpenChange={setWizardOpen}
              contrato={c}
              detalle={det}
              saldoActual={saldoActual}
              onComplete={completarRescision}
            />
          </TabsContent>

          {/* 7 · Arreglos */}
          <TabsContent value="arreglos" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">{arreglos.length} arreglo(s) en este contrato</p>
              <Button size="sm" className="btn-brand rounded-md" data-testid="ficha-nuevo-arreglo-button" onClick={() => setNuevoOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" /> Reportar arreglo
              </Button>
            </div>
            {arreglos.length === 0 && (
              <Card className="p-6 rounded-md border-slate-200 shadow-none text-center">
                <p className="text-sm text-slate-500">No hay arreglos registrados en este contrato.</p>
              </Card>
            )}
            {arreglos.map((a) => (
              <Card key={a.id} className="rounded-md border-slate-200 shadow-none overflow-hidden" data-testid={`ficha-arreglo-${a.id}`}>
                <div className="flex gap-4 p-4">
                  <div className="flex gap-2 shrink-0">
                    <PhotoInput label="Antes" value={a.fotos.antes} onChange={(d) => setFoto(a.id, "antes", d)} testId={`ficha-arreglo-foto-antes-${a.id}`} />
                    <PhotoInput label="Después" value={a.fotos.despues} onChange={(d) => setFoto(a.id, "despues", d)} testId={`ficha-arreglo-foto-despues-${a.id}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">{a.titulo}</p>
                      <StatusBadge estado={a.estado} />
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{a.ambiente} · reportado {fechaCorta(a.fechaReporte)} · urgencia {a.urgencia.toLowerCase()}</p>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                      <span>Paga: <span className="font-medium text-slate-800">{a.paga}</span></span>
                      <span>Proveedor: <span className="font-medium text-slate-800">{a.proveedor}</span></span>
                      <span>Presupuesto: <span className="font-medium text-slate-800">{pesos(a.presupuesto)}</span></span>
                      <span className="flex items-center gap-1.5">Aprobación: <StatusBadge estado={a.aprobacion} /></span>
                    </div>
                    {a.aprobacion === "Pendiente" && (
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" className="btn-brand rounded-md" data-testid={`ficha-arreglo-aprobar-${a.id}`} onClick={() => aprobar(a)}>Aprobar</Button>
                        <Button size="sm" variant="outline" className="rounded-md" data-testid={`ficha-arreglo-rechazar-${a.id}`} onClick={() => rechazar(a)}>Rechazar</Button>
                      </div>
                    )}
                    {a.aprobacion === "Aprobado" && (
                      <p className="mt-2 text-[11px] text-emerald-700">El costo aprobado se imputa en la cuenta corriente del contrato.</p>
                    )}
                    {a.estado !== "Terminado" && (
                      <div className="mt-3">
                        <Button size="sm" variant="outline" className="rounded-md" data-testid={`ficha-arreglo-presupuesto-${a.id}`} onClick={() => pedirPresupuesto(a)}>
                          <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Pedir presupuesto al proveedor
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}

            <Dialog open={nuevoOpen} onOpenChange={setNuevoOpen}>
              <DialogContent className="max-w-md" data-testid="ficha-nuevo-arreglo-dialog">
                <DialogHeader>
                  <DialogTitle>Reportar arreglo</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Título</Label>
                    <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Pérdida en canilla de cocina" className="rounded-md mt-1" data-testid="ficha-nuevo-arreglo-titulo" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Ambiente</Label>
                      <Select value={form.ambiente} onValueChange={(v) => setForm({ ...form, ambiente: v })}>
                        <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Living-comedor", "Cocina", "Dormitorio principal", "Baño", "Balcón", "Otro"].map((x) => (
                            <SelectItem key={x} value={x}>{x}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Urgencia</Label>
                      <Select value={form.urgencia} onValueChange={(v) => setForm({ ...form, urgencia: v })}>
                        <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Alta">Alta</SelectItem>
                          <SelectItem value="Media">Media</SelectItem>
                          <SelectItem value="Baja">Baja</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Quién paga</Label>
                      <Select value={form.paga} onValueChange={(v) => setForm({ ...form, paga: v })}>
                        <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Inquilino">Inquilino</SelectItem>
                          <SelectItem value="Propietario">Propietario</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Presupuesto ($)</Label>
                      <Input type="number" value={form.presupuesto} onChange={(e) => setForm({ ...form, presupuesto: e.target.value })} placeholder="0" className="rounded-md mt-1" data-testid="ficha-nuevo-arreglo-presupuesto" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Proveedor (opcional)</Label>
                    <Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} placeholder="Por asignar" className="rounded-md mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Fotos (opcional)</Label>
                    <div className="flex gap-3 mt-1">
                      <PhotoInput label="Antes" value={form.fotoAntes} onChange={(d) => setForm({ ...form, fotoAntes: d })} testId="ficha-nuevo-arreglo-foto-antes" />
                      <PhotoInput label="Después" value={form.fotoDespues} onChange={(d) => setForm({ ...form, fotoDespues: d })} testId="ficha-nuevo-arreglo-foto-despues" />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-md" onClick={() => setNuevoOpen(false)}>Cancelar</Button>
                  <Button className="btn-brand rounded-md" data-testid="ficha-nuevo-arreglo-submit" onClick={agregarArreglo}>Reportar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* 8 · Línea de tiempo */}
          <TabsContent value="timeline" className="mt-4">
            <div className="space-y-0" data-testid="ficha-timeline">
              {det.timeline.map((ev, i) => {
                const Icon = TIMELINE_ICONS[ev.tipo] || Clock;
                return (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-light text-brand shrink-0">
                        <Icon className="h-4 w-4" />
                      </span>
                      {i < det.timeline.length - 1 && <span className="w-px flex-1 bg-slate-200 my-1" />}
                    </div>
                    <div className="pb-5">
                      <p className="text-xs text-slate-400">{fechaCorta(ev.fecha)}</p>
                      <p className="text-sm text-slate-800">{ev.texto}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* 9 · Documentos */}
          <TabsContent value="documentos" className="mt-4">
            <div className="divide-y divide-slate-100 rounded-md border border-slate-200" data-testid="ficha-documentos">
              {det.documentos.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{doc.nombre}</p>
                      <p className="text-[11px] text-slate-400">{doc.tipo}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-md shrink-0" data-testid={`ficha-doc-${doc.id}`} onClick={() => descargarDoc(doc)}>
                    <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function Sub({ t }) {
  return <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-2 mt-1 first:mt-0">{t}</p>;
}

function Kv({ l, v, icon: Icon }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-sm">
      <span className="text-slate-500 flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}{l}</span>
      <span className="font-medium text-slate-800 text-right">{v}</span>
    </div>
  );
}

function CostoRow({ concepto, paga, alDia, monto }) {
  return (
    <TableRow>
      <TableCell className="text-sm text-slate-700">{concepto}</TableCell>
      <TableCell className="text-sm text-slate-600">{paga}</TableCell>
      <TableCell><StatusBadge estado={alDia ? "Al día" : "Pendiente"} /></TableCell>
      <TableCell className="text-right text-sm font-medium">{pesos(monto)}</TableCell>
    </TableRow>
  );
}

function NumCell({ value, onChange, testId }) {
  return (
    <Input
      type="number"
      value={value || 0}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-8 w-28 ml-auto rounded-md text-right text-sm"
    />
  );
}

function recalcSaldos(rows) {
  let acc = 0;
  return rows.map((m) => {
    acc += (Number(m.debe) || 0) + (Number(m.punitorios) || 0) + (Number(m.arreglos) || 0);
    return { ...m, saldo: acc };
  });
}
